import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, Form, Input, Select, Tag, Space, Checkbox, Alert, Button, Typography, Image, Spin, Upload, message,
} from 'antd';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { orderApi, priorityApi, customerApi } from '../services/api';

const { TextArea } = Input;
const { Text } = Typography;

const priorityColor = { urgent: 'red', high: 'orange', normal: 'blue', low: 'default' };
const priorityText = { urgent: '紧急重要', high: '一般重要', normal: '普通', low: '普通' };

// 必填项红字标注（加粗加大）
const SalesMark = () => (
  <span style={{ color: '#ff4d4f', fontWeight: 800, fontSize: 15, marginInlineStart: 8 }}>
    销售/客服填写
  </span>
);

// 客户等级 → 客户类型维度选项标签（与后端 CUSTOMER_LEVEL_TO_OPTION 一致）
const CUSTOMER_LEVEL_OPTION_LABEL = {
  important: '重要级客户',
  medium: '中等客户',
  normal: '一般普通客户',
};

/**
 * 新建工单弹窗（与「待跟进调度-新建工单」完全同一套表单与逻辑）
 * @param open 是否打开
 * @param onClose 关闭回调
 * @param onCreated 创建成功回调
 * @param initialValues 预填值（如科维斯订单「生成调度」带入的字段）
 */
export default function CreateOrderModal({ open, onClose, onCreated, initialValues }) {
  const [form] = Form.useForm();
  const [dimensions, setDimensions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [judgePreview, setJudgePreview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [images, setImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);

  const customerDim = useMemo(() => dimensions.find((d) => d.code === 'customer'), [dimensions]);

  const loadCustomers = () => {
    customerApi.list({ keyword: '' }).then((d) => setCustomers(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const handleCustomerChange = (name) => {
    const custOpts = customerDim?.options || [];
    if (!name) {
      setSelectedOptionIds((prev) => prev.filter((id) => !custOpts.some((o) => o.id === id)));
      return;
    }
    const c = customers.find((x) => x.name === name);
    const label = CUSTOMER_LEVEL_OPTION_LABEL[c?.level];
    const opt = custOpts.find((o) => o.label === label);
    setSelectedOptionIds((prev) => [
      ...prev.filter((id) => !custOpts.some((o) => o.id === id)),
      ...(opt ? [opt.id] : []),
    ]);
  };

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialValues) form.setFieldsValue(initialValues);
    setSelectedOptionIds([]);
    setJudgePreview(null);
    setImages(initialValues?.issue_images || []);
    loadCustomers();
    priorityApi.getDimensions().then((d) => {
      setDimensions(d.dimensions || []);
      setPolicies(d.policies || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 勾选维度后实时判级预览（300ms 防抖）
  useEffect(() => {
    if (!open) return;
    if (!selectedOptionIds.length) { setJudgePreview(null); return; }
    const t = setTimeout(() => {
      priorityApi.evaluate(selectedOptionIds).then(setJudgePreview).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [selectedOptionIds, open]);

  const toggleDimensionOptions = (dim, vals) => {
    setSelectedOptionIds((prev) => [
      ...prev.filter((id) => !dim.options.some((o) => o.id === id)),
      ...vals,
    ]);
  };

  const handleCreate = async (values) => {
    try {
      const vals = { ...values };
      // 手机端不便打字：只传截图时自动补一句占位描述，保证弹窗文案可读
      if ((!vals.core_issue || !vals.core_issue.trim()) && images.length) {
        vals.core_issue = '核心问题详见工单截图';
      }
      await orderApi.create({ ...vals, dimension_options: selectedOptionIds, issue_images: images });
      message.success('工单创建成功，优先级已由系统自动判定');
      onCreated && onCreated();
      onClose && onClose();
    } catch (e) {}
  };

  // 相册上传（移动端无粘贴：直接从手机相册选图，与粘贴截图走同一链路）
  const handleAlbumFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith('image/'));
    if (!files.length) return;
    setUploadingImg(true);
    try {
      const urls = [];
      for (const f of files) {
        const d = await orderApi.uploadImage(f);
        urls.push(d.url);
      }
      setImages((prev) => [...prev, ...urls]);
      message.success(`已上传 ${urls.length} 张截图`);
    } catch (err) {
      // 拦截器已提示
    } finally {
      setUploadingImg(false);
    }
  };

  // 粘贴截图上传
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f && f.type.startsWith('image/')) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    setUploadingImg(true);
    try {
      const urls = [];
      for (const f of files) {
        const d = await orderApi.uploadImage(f);
        urls.push(d.url);
      }
      setImages((prev) => [...prev, ...urls]);
      message.success(`截图已粘贴（${urls.length} 张）`);
    } catch (err) {
      // 拦截器已提示
    } finally {
      setUploadingImg(false);
    }
  };

  const customerLevelColor = { important: 'red', medium: 'orange', normal: 'blue' };

  return (
    <Modal
      title="新建工单"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleCreate}>
        <Form.Item name="order_no" label={<span>订单号<SalesMark /></span>} rules={[{ required: true, message: '请输入订单号' }]}>
          <Input placeholder="订单号/BOL号/PRO号" />
        </Form.Item>
        <Form.Item
          name="customer_name"
          label={<span>客户名称<SalesMark /></span>}
          rules={[{ required: true, message: '请从客户库选择客户' }]}
          extra="从客户库下拉或输入名称检索选择；客户类型按客户等级自动判定，不可手动修改。新客户请先到「客户管理-客户添加」录入。"
        >
          <Select
            showSearch
            allowClear
            placeholder="输入客户名称检索或下拉选择"
            optionFilterProp="searchtext"
            filterOption={(input, option) =>
              (option?.searchtext || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleCustomerChange}
            notFoundContent="客户库中无此客户，请先到「客户管理-客户添加」录入"
            options={customers.map((c) => ({
              value: c.name,
              searchtext: c.name,
              label: (
                <Space size={6}>
                  <span>{c.name}</span>
                  <Tag color={customerLevelColor[c.level] || 'default'} style={{ marginInlineEnd: 0 }}>
                    {c.level_label}
                  </Tag>
                </Space>
              ),
            }))}
          />
        </Form.Item>
        <Form.Item name="business_type" label="业务类型" initialValue="ltl">
          <Select options={[{ value: 'ltl', label: 'LTL零担' }, { value: 'ftl', label: 'FTL整车' }]} />
        </Form.Item>
        <Form.Item
          name="core_issue"
          label={<span>核心问题/异常情况<SalesMark /></span>}
          rules={[{
            validator: (_, v) =>
              ((v && v.trim()) || images.length)
                ? Promise.resolve()
                : Promise.reject(new Error('请描述核心问题，或点「相册上传」选择截图')),
          }]}
          extra={
            <Space size={8} style={{ marginTop: images.length || uploadingImg ? 8 : 4, width: '100%' }} align="center" wrap>
              <Upload
                accept="image/*"
                multiple
                showUploadList={false}
                beforeUpload={(file) => { handleAlbumFiles([file]); return false; }}
              >
                <Button size="small" icon={<PictureOutlined />}>相册上传</Button>
              </Upload>
              <span style={{ color: '#8c8c8c' }}>
                手机从相册选截图，电脑可粘贴（Ctrl/⌘+V）{uploadingImg && <Spin size="small" style={{ marginInlineStart: 8 }} />}
              </span>
            </Space>
          }
        >
          <div onPaste={handlePaste}>
            <TextArea rows={3} placeholder="描述订单的核心问题或异常情况" />
            {(images.length > 0 || uploadingImg) && (
              <Image.PreviewGroup>
                <Space wrap size={8} style={{ marginTop: 8 }}>
                  {images.map((u) => (
                    <div key={u} style={{ position: 'relative', width: 72, height: 72 }}>
                      <Image src={u} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        title="删除"
                        onClick={() => setImages((prev) => prev.filter((x) => x !== u))}
                        style={{ position: 'absolute', top: -6, right: -8, padding: 0, background: '#fff', borderRadius: '50%' }}
                      />
                    </div>
                  ))}
                </Space>
              </Image.PreviewGroup>
            )}
          </div>
        </Form.Item>
        <Form.Item name="handling_strategy" label={<span>核心处理思路 <span style={{color:'#ff4d4f',fontWeight:600,fontSize:13}}>（调度填写）</span></span>}>
          <TextArea rows={3} placeholder="与客服销售沟通后确定的处理思路（调度员填写，可选）" />
        </Form.Item>
        <Form.Item name="follow_up_plan" label={<span>跟进策略 <span style={{color:'#ff4d4f',fontWeight:600,fontSize:13}}>（调度填写）</span></span>}>
          <TextArea rows={2} placeholder="具体的跟进计划和步骤（调度员填写，可选）" />
        </Form.Item>
        <Form.Item name="carrier" label="卡司/承运商">
          <Input placeholder="卡司名称" />
        </Form.Item>
        <Form.Item name="carrier_platform" label="卡车平台">
          <Input placeholder="如: Truckstop, DAT等" />
        </Form.Item>
        <Form.Item name="pickup_location" label="提货地址">
          <Input placeholder="提货地址" />
        </Form.Item>
        <Form.Item name="delivery_location" label="送货地址">
          <Input placeholder="送货地址" />
        </Form.Item>
        <Form.Item name="estimated_delivery" label="提货时间">
          <Input placeholder="提货时间" />
        </Form.Item>

        {/* 优先级维度勾选：系统自动判级，不由人工判断 */}
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', marginBottom: 16, background: '#fafafa' }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            优先级判定维度 <Tag color="blue">系统自动判级</Tag>
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            勾选订单适用的维度，系统自动判定优先级并固定提醒间隔；无需人工选择优先级。
          </Text>
          {dimensions.map((d) => (
            <div key={d.id} style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>{d.name}：</Text>
              {d.code === 'customer' && (
                <Tag color="gold" style={{ marginInlineStart: 4 }}>随客户自动判定，不可修改</Tag>
              )}
              <Checkbox.Group
                style={{ marginTop: 4, width: '100%' }}
                disabled={d.code === 'customer'}
                options={d.options.filter((o) => o.trigger_level !== 'none').map((o) => ({ label: o.label, value: o.id }))}
                value={selectedOptionIds.filter((id) => d.options.some((o) => o.id === id))}
                onChange={(vals) => toggleDimensionOptions(d, vals)}
              />
            </div>
          ))}
          {judgePreview && (
            <Alert
              style={{ marginTop: 8 }}
              type={judgePreview.priority === 'urgent' ? 'error' : judgePreview.priority === 'high' ? 'warning' : 'info'}
              message={
                <Space>
                  <span>系统判定：</span>
                  <Tag color={priorityColor[judgePreview.priority]}>{priorityText[judgePreview.priority]}</Tag>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {policies.find((p) => p.priority === judgePreview.priority)?.follow_up_text || ''}
                    {' / '}
                    每 {policies.find((p) => p.priority === judgePreview.priority)?.reminder_hours || '-'} 小时弹窗提醒
                  </span>
                </Space>
              }
              description={judgePreview.reasons?.join('；')}
            />
          )}
        </div>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">创建</Button>
            <Button onClick={onClose}>取消</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
